/**
 * EventModal — extends XModal to AJAX-fetch linked Shopify page content
 * and auto-generate a Table of Contents from h2/h3 headings.
 */
class EventModal extends XModal {
  constructor() {
    super();
    this._loaded = false;
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }

  get pageUrl() {
    return this.dataset.pageUrl;
  }

  get bodyEl() {
    return this.querySelector('.events-modal__body');
  }

  beforeShow() {
    if (!this._loaded && this.pageUrl) {
      this.setAttribute('data-loading', '');
      this.fetchPageContent();
    }
  }

  async fetchPageContent() {
    try {
      const response = await fetch(this.pageUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract the main page content
      const mainContent = doc.querySelector('.page.rte') ||
                          doc.querySelector('[id^="shopify-section-main"] .rte') ||
                          doc.querySelector('main .rte') ||
                          doc.querySelector('main .page-width');

      if (!mainContent) {
        this.bodyEl.innerHTML = '<p>Content could not be loaded.</p>';
        this.removeAttribute('data-loading');
        this._loaded = true;
        return;
      }

      // Extract title from the page
      const pageTitle = doc.querySelector('h1')?.textContent?.trim() || '';

      // Get headings for TOC before modifying content
      const contentHtml = mainContent.innerHTML;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentHtml;

      // Add IDs to headings for anchor links
      const headings = tempDiv.querySelectorAll('h2, h3');
      const tocItems = [];

      headings.forEach((heading, index) => {
        const id = `event-section-${this.id}-${index}`;
        heading.id = id;
        tocItems.push({
          id,
          text: heading.textContent.trim(),
          tag: heading.tagName.toLowerCase()
        });
      });

      // Find hero image (first image in content)
      const heroImg = tempDiv.querySelector('img');
      let heroImageHtml = '';
      if (heroImg) {
        heroImageHtml = `<img class="events-modal__hero-image" src="${heroImg.src}" alt="${heroImg.alt || ''}" loading="lazy">`;
        heroImg.remove();
      }

      // Build TOC HTML
      let tocHtml = '';
      if (tocItems.length > 0) {
        tocHtml = `
          <div class="events-modal__toc">
            <p class="events-modal__toc-title">Table of Contents</p>
            <ul class="events-modal__toc-list">
              ${tocItems.map(item => `<li><a href="#${item.id}" data-toc-link>${item.text}</a></li>`).join('')}
            </ul>
          </div>
        `;
      }

      // Assemble modal body
      this.bodyEl.innerHTML = `
        ${pageTitle ? `<h2 class="events-modal__hero-heading">${pageTitle}</h2>` : ''}
        ${heroImageHtml}
        ${tocHtml}
        <div class="events-modal__page-content">${tempDiv.innerHTML}</div>
      `;

      // Bind TOC smooth scroll
      this.bodyEl.querySelectorAll('[data-toc-link]').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const target = this.bodyEl.querySelector(link.getAttribute('href'));
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });

      this._loaded = true;
    } catch (err) {
      console.error('EventModal: Failed to load page content', err);
      this.bodyEl.innerHTML = '<p>Unable to load event details. Please try again.</p>';
    } finally {
      this.removeAttribute('data-loading');
    }
  }
}

if (!customElements.get('event-modal')) {
  customElements.define('event-modal', EventModal);
}

/**
 * Drag-to-scroll for desktop users on .events-row__track elements.
 */
document.querySelectorAll('.events-row__track').forEach((track) => {
  let isDown = false;
  let startX;
  let scrollLeft;

  track.addEventListener('dragstart', (e) => e.preventDefault());

  track.addEventListener('mousedown', (e) => {
    isDown = true;
    track.classList.add('is-dragging');
    startX = e.pageX - track.offsetLeft;
    scrollLeft = track.scrollLeft;
  });

  track.addEventListener('mouseleave', () => {
    isDown = false;
    track.classList.remove('is-dragging');
  });

  track.addEventListener('mouseup', () => {
    isDown = false;
    track.classList.remove('is-dragging');
  });

  track.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - track.offsetLeft;
    const walk = (x - startX) * 1.5;
    track.scrollLeft = scrollLeft - walk;
  });
});

/**
 * Equalize the height of all event info panels within each row
 * so that .events-row__event-info and .events-row__event-overlay-info
 * are the same height across every card in the row.
 */
function equalizeEventInfoHeights() {
  document.querySelectorAll('.events-row__track').forEach((track) => {
    const infos = track.querySelectorAll('.events-row__event-info, .events-row__event-overlay-info');

    // Reset heights first so we measure natural content height
    infos.forEach((el) => { el.style.height = ''; });

    let maxHeight = 0;
    infos.forEach((el) => {
      maxHeight = Math.max(maxHeight, el.offsetHeight);
    });

    if (maxHeight > 0) {
      infos.forEach((el) => { el.style.height = maxHeight + 'px'; });
    }
  });
}

equalizeEventInfoHeights();

window.addEventListener('resize', equalizeEventInfoHeights);
